import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';

/** Question-mark help control - tooltip is portaled so drawers never clip it. */
export function ScoreCardHelp({
  text,
  iconSize = 14,
  className,
}: {
  text: string;
  iconSize?: number;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);

  const show = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setTip({ x: r.left + r.width / 2, y: r.top });
  }, []);

  const hide = useCallback(() => setTip(null), []);

  const tipX =
    tip == null
      ? 0
      : Math.min(Math.max(tip.x, 150), window.innerWidth - 150);

  return (
    <>
      <span
        ref={ref}
        className={className ? `sc-help ${className}` : 'sc-help'}
        tabIndex={0}
        role="button"
        aria-label={`About this metric: ${text}`}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
          }
        }}
      >
        <Icon name="helpCircle" size={iconSize} stroke={2} />
      </span>
      {tip &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="sc-help-tip sc-help-tip-portal"
            style={{ left: tipX, top: tip.y }}
            role="tooltip"
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}
